import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { vizToneColor } from '../_viz/viz-tone';
import {
  gmAtStakeKpi,
  gmStripCells,
  type GmMiniItem,
  type GmTabId,
  type LandingStripCell,
} from './GmLeadBand.transform';

/**
 * The GM landing's lead band (locked comp `gm-landing.html` B): the three
 * landing views as a strip-as-tabs in the Advisory idiom (`PipelineStrip`
 * tabs mode, same cell recipe), beside the one exec KPI that opens Advisory.
 */
export function GmLeadBand({
  state,
  tab,
  recorded,
  fmeaLearning,
  onTabChange,
  advisoryHref,
}: {
  state: SessionState;
  tab: GmTabId;
  /** Escalation cards already recorded; the Escalations cell counts the open ones. */
  recorded: ReadonlySet<string>;
  /** K13b: a waiting FMEA proposal is an open decision on the Escalations cell. */
  fmeaLearning?: FmeaLearningSessionState;
  onTabChange: (tab: GmTabId) => void;
  advisoryHref: string;
}) {
  const cells = gmStripCells(state, recorded, fmeaLearning);
  const atStake = gmAtStakeKpi(state);
  return (
    <div id="walkthrough-gm-lead" className="grid grid-cols-12 gap-3" data-gm-lead-band>
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Landing views"
        className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
      >
        <LandingStripTabs
          cells={cells}
          selected={tab}
          onSelect={onTabChange}
          dataPrefix="gm"
          panelId="gm-landing-panel"
          columnsClassName="sm:grid-cols-3"
        />
      </GlassCard>
      <Link
        to={advisoryHref}
        data-gm-at-stake
        aria-label={`${atStake.label} · ${atStake.figure}${atStake.unit} · open Advisory`}
        className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
      >
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{atStake.label}</span>
          {/* The way on sits top-right, as the health cards' chevron does: the D20
              split under the figure takes the comp's bottom line, and the view keeps its fold. */}
          <span className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-ink-primary">
            Advisory
            <ArrowUpRight className="size-3.5" aria-hidden="true" />
          </span>
        </span>
        <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
          <span data-gm-at-stake-figure>{atStake.figure}</span>
          <span className="text-xs font-semibold tracking-normal text-muted">{atStake.unit}</span>
        </span>
        <span className="text-xs leading-snug tabular-nums text-muted">
          <span className="font-semibold text-ink-secondary">{atStake.comparisonLead}</span>
          {atStake.comparisonRest}
        </span>
      </Link>
    </div>
  );
}

/** One hairline-split figure row inside a single card (comp `.minirow`). */
export function GmMiniRow({
  items,
  columnsClassName = 'sm:grid-cols-3',
}: {
  items: readonly GmMiniItem[];
  /** Grid columns at and above `sm`. Default three, the GM and Superintendent rows. */
  columnsClassName?: string;
}) {
  return (
    <div
      className={cn('card-matte-2 grid grid-cols-1 rounded-2xl', columnsClassName)}
      data-gm-mini-row
    >
      {items.map((item) => (
        <div
          key={item.id}
          className="flex min-w-0 flex-col gap-0.5 border-b border-[var(--hairline)] px-3.5 py-2 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
        >
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.07em] text-muted">
            <span
              aria-hidden="true"
              className="inline-block size-[7px] shrink-0 rounded-full"
              style={{ backgroundColor: vizToneColor(item.tone) }}
            />
            {item.label}
            {item.link ? (
              <Link
                to={item.link.to}
                className="ml-auto inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[12.5px] font-semibold normal-case tracking-normal text-ink-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
                data-gm-mini-link={item.id}
              >
                {item.link.label}
                <ArrowUpRight className="size-3.5" aria-hidden="true" />
              </Link>
            ) : null}
          </span>
          <span className="text-[17px] font-semibold leading-tight tabular-nums text-ink-primary">
            {item.value}
            {item.unit ? (
              <>
                {' '}
                <span className="text-xs font-semibold text-muted">{item.unit}</span>
              </>
            ) : null}
          </span>
          {item.rule ? (
            <span
              className="relative mt-1 block h-1 rounded-sm bg-[var(--hairline)]"
              title={item.rule.tickTitle}
              data-gm-mini-rule
            >
              <i
                className="absolute inset-y-0 left-0 rounded-sm bg-[var(--ink-secondary)]"
                style={{ width: `${item.rule.filledPct}%` }}
              />
              <u
                className="absolute -bottom-[3px] -top-[3px] w-[1.5px] bg-[var(--state-info)]"
                style={{ left: `${item.rule.tickPct}%` }}
              />
            </span>
          ) : null}
          {/* `balance` keeps a wrapping caption off a one-word last line (the
              Pb recovery caption left `days` alone at 1470; polish.P26 r1). */}
          <span
            className={cn(
              'text-xs leading-snug tabular-nums text-muted [text-wrap:balance]',
              item.rule && 'flex justify-between gap-2',
            )}
          >
            {item.rule ? (
              <>
                <span>
                  {item.captionLead ? <span className="font-semibold text-ink-secondary">{item.captionLead}</span> : null}
                  {item.caption}
                </span>
                <span>{item.rule.aside}</span>
              </>
            ) : (
              <>
                {item.captionLead ? <span className="font-semibold text-ink-secondary">{item.captionLead}</span> : null}
                {item.captionDelta ? (
                  <>
                    {' · '}
                    <span className={cn('font-semibold', FLAG_TONE[item.captionDelta.tone])}>{item.captionDelta.text}</span>
                  </>
                ) : null}
                {item.caption}
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

const FLAG_TONE: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
};

/**
 * The landing views as one strip of tabs (locked comps `gm-landing.html` B and
 * `landing-superintendent.html` C): caps name, primary figure, comparison and
 * actor chip per cell, hairline splits with a chevron notch, the selected cell
 * washed with a 3px crimson bottom rule. Data attributes read
 * `data-<prefix>-strip-cell|figure|comparison` and tab ids `<prefix>-tab-<id>`.
 */
export function LandingStripTabs<Id extends string>({
  cells,
  selected,
  onSelect,
  dataPrefix,
  panelId,
  columnsClassName,
}: {
  cells: readonly LandingStripCell<Id>[];
  selected: Id;
  onSelect: (id: Id) => void;
  dataPrefix: string;
  panelId: string;
  /** Grid columns at and above `sm` (`sm:grid-cols-3`). */
  columnsClassName: string;
}) {
  return (
    <div role="tablist" aria-label="Landing view" className={cn('grid grid-cols-1', columnsClassName)}>
      {cells.map((cell) => {
        const isSelected = cell.id === selected;
        return (
          <button
            key={cell.id}
            type="button"
            role="tab"
            id={`${dataPrefix}-tab-${cell.id}`}
            aria-selected={isSelected}
            aria-controls={panelId}
            {...{ [`data-${dataPrefix}-strip-cell`]: cell.id }}
            onClick={() => onSelect(cell.id)}
            // K18 r1: pr-1 (with the figure's tighter gap-x-1) lets the FY
            // outlook cell keep `US$40.43M EBITDA · central` on one figure
            // line at 1280 without unwrapping the comparison beneath it.
            className={`group relative flex min-h-[7.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] py-3 pl-4 pr-1 text-left transition-colors last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)] sm:border-b-0 after:pointer-events-none after:absolute after:right-[-6px] after:top-1/2 after:z-10 after:hidden after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rotate-45 after:border-r after:border-t after:border-[var(--hairline-strong)] after:content-[''] last:after:hidden sm:after:block sm:last:after:hidden ${
              isSelected
                ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] shadow-[inset_0_-3px_0_var(--color-accent-500)] after:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]'
                : 'bg-[var(--surface-2)] after:bg-[var(--surface-2)] hover:bg-[var(--surface-1)] hover:after:bg-[var(--surface-1)]'
            }`}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.name}</span>
            <span
              className="flex flex-wrap items-baseline gap-x-1 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary"
              {...{ [`data-${dataPrefix}-strip-figure`]: cell.id }}
            >
              {cell.figureFlag ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'mr-1 inline-block size-[7px] shrink-0 self-center rounded-full',
                    cell.figureFlag === 'bad' ? 'bg-[var(--state-blocked)]' : 'bg-[var(--state-warning)]',
                  )}
                />
              ) : null}
              {cell.figure}
              {cell.figureContext ? (
                <span className="text-xs font-semibold tracking-normal text-muted">{cell.figureContext}</span>
              ) : null}
            </span>
            <span className="text-xs leading-snug tabular-nums text-muted" {...{ [`data-${dataPrefix}-strip-comparison`]: cell.id }}>
              <span className={cn('font-semibold', cell.comparisonLeadTone ? FLAG_TONE[cell.comparisonLeadTone] : 'text-ink-secondary')}>
                {cell.comparisonLead}
              </span>
              {cell.comparisonRest}
              {cell.comparisonFlag ? (
                <span className={cn('font-semibold', FLAG_TONE[cell.comparisonFlag.tone])}>{cell.comparisonFlag.text}</span>
              ) : null}
            </span>
            <span className="mt-auto flex min-w-0 flex-wrap items-center gap-1 pt-1">
              <ActorChip actor={cell.actor}>{cell.actorLabel}</ActorChip>
              {cell.moreActors?.map((more) => (
                <ActorChip key={`${more.actor}-${more.label ?? ''}`} actor={more.actor}>
                  {more.label}
                </ActorChip>
              ))}
              {cell.provenance ? (
                <span
                  title={cell.provenance.title}
                  className="inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-1.5 text-[10.5px] leading-4 text-muted"
                >
                  {cell.provenance.text}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
